import { Icons } from 'components/Icon';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { IdentificationTypeEnum } from '../types/user.enum.type';
import IdentificationCard from 'components/Cards/IdentificationCard';
import TextField from 'components/Form/TextField';
import Button from 'components/Form/Button';
import { IdentityType } from '../types/on-boarding.type';
import { useIdentityVerificationUpdate } from 'common/queries-and-mutations/user';
import { findNextPage, findPreviousPage, getImageExtension } from 'utils';
import { selectAccountDetails } from 'selectors/account-selector';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useNavigate } from 'react-router-dom';
import { STEPS } from 'constants/steps';
import { fetchUserInformation } from 'thunks/account-thunk';
import InfoBar from './InfoBar';
import verification from '../assets/verification.png';

export default function IdentificationType() {
  const [file, setFile] = useState<File>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isError } = useIdentityVerificationUpdate();
  const { user } = useAppSelector(selectAccountDetails);
  const navigate = useNavigate();
  const planAllowPage = user && JSON.parse(user?.planAllowPage);
  const dispatch = useAppDispatch();
  const [showCompleted, setShowCompleted] = useState(
    user.identificationId || user.identificationPath === 'in-review'
  );
  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const formik = useFormik({
    initialValues: {
      identificationType: user.identificationType,
      identificationId: user.identificationId,
      identificationBase64: user.identificationBase64,
      base64FileName:
        user.identificationPath === 'in-review' ? 'in-review' : '',
      passportLastName: user.passportLastName
    },
    // validationSchema: Yup.object({
    //   identificationType: Yup.string().required(
    //     'Select your identification type'
    //   ),
    //   passportLastName: Yup.string().when('identificationType', {
    //     is: IdentificationTypeEnum.INTERNATION_PASSPORT,
    //     then: Yup.string().required('Passport lastname is required').nullable(),
    //     otherwise: Yup.string()
    //   })
    // }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onIdentitySubmit(values);
    }
  });

  const handleOnSelectIdentityType = (value: string) => {
    formik.setFieldValue('identificationType', value);
    formik.setFieldValue('identificationId', '');
  };

  const handleImage = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.currentTarget as HTMLInputElement;
    const file: File = (target.files as FileList)[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = async () => {
        if (reader.result) {
          formik.setFieldValue(
            'identificationBase64',
            reader.result.toString()
          );
          setFile(file);
        }
      };
    }
  };

  const onIdentitySubmit = (formValue: IdentityType) => {
    if (
      !file &&
      formValue.identificationType === user.identificationType &&
      !formik.values.identificationId
    ) {
      navigate(findNextPage(planAllowPage, STEPS.INDENTITY_VERIFICATION));
      return;
    }
    if (
      !formik.values.identificationId &&
      !formik.values.identificationBase64
    ) {
      formik.setFieldError(
        'identificationBase64',
        'At least one of Identification or identification Path should not be empty'
      );
    }
    let fileName = '';
    if (file) {
      fileName = `${user.id}_${
        formik.values.identificationType
      }${getImageExtension(file.name)}`;
    }
    formValue.identificationId =
      formValue.identificationId || user.identificationId;
    if (formValue.identificationBase64) {
      formValue.identificationId = '';
    }
    if (formValue.identificationId) {
      formValue.identificationBase64 = '';
    }

    if (
      formValue.identificationId === user.identificationId ||
      !formValue.identificationBase64
    ) {
      navigate(findNextPage(planAllowPage, STEPS.INDENTITY_VERIFICATION));
      return;
    }

    mutate({ ...formValue, base64FileName: fileName });
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.INDENTITY_VERIFICATION));
    } else if (isError) {
      setIsSubmitting(false);
    }
  }, [dispatch, isError, isSuccess, navigate, planAllowPage]);

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return false;
    }
    mutate(formik.values);
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.INDENTITY_VERIFICATION));
  };

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={3}
        stepPercentage={STEPS.INDENTITY_VERIFICATION}
        timelineStep={1}
      />
      <div className="flex flex-col items-center justify-center">
        <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
          Select Identification Type
        </p>

        <form onSubmit={formik.handleSubmit} className="mx-10 w-full">
          {showCompleted ? (
            <div className="mt-10 flex items-center justify-center space-x-4">
              <div className="h-32 w-32 overflow-hidden rounded-full">
                <img
                  src={verification}
                  alt="Your Image Alt Text"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-col items-center">
                <p className="rounded-lg bg-gray-200 p-4">
                  Your Identity has been submitted for review
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Modify"
                  type="button"
                  onClick={() => setShowCompleted(false)}
                ></Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="mt-5 flex flex-wrap justify-center gap-5">
                <IdentificationCard
                  onSelect={handleOnSelectIdentityType}
                  selectedIdentity={formik.values.identificationType}
                  fieldValue={IdentificationTypeEnum.NATIONAL_ID}
                  fieldTitle="National ID"
                  fieldIcon={Icons.NationalID}
                />
                <IdentificationCard
                  onSelect={handleOnSelectIdentityType}
                  selectedIdentity={formik.values.identificationType}
                  fieldValue={IdentificationTypeEnum.INTERNATION_PASSPORT}
                  fieldTitle="Int’l Passport"
                  fieldIcon={Icons.Passport}
                />
                <IdentificationCard
                  onSelect={handleOnSelectIdentityType}
                  selectedIdentity={formik.values.identificationType}
                  fieldValue={IdentificationTypeEnum.DRIVER_LICENSE}
                  fieldTitle="Drivers License"
                  fieldIcon={Icons.DriverLicense}
                />
                <IdentificationCard
                  onSelect={handleOnSelectIdentityType}
                  selectedIdentity={formik.values.identificationType}
                  fieldValue={IdentificationTypeEnum.VOTERS_CARD}
                  fieldTitle="Voter's Card"
                  fieldIcon={Icons.VotersCard}
                />
                <IdentificationCard
                  onSelect={handleOnSelectIdentityType}
                  selectedIdentity={formik.values.identificationType}
                  fieldValue={IdentificationTypeEnum.BVN}
                  fieldTitle="BVN"
                  fieldIcon={Icons.BVN}
                />
              </div>
              <div className="mt-3 px-10 text-center text-xs capitalize text-red-600">
                {formik.errors.identificationType && (
                  <p>Select your identification type</p>
                )}
              </div>
              {formik.values.identificationType && (
                <div className="items-centers flex flex-col">
                  <div className="flex items-center justify-center space-x-4 rounded-md p-8">
                    {formik.values.identificationType !==
                      IdentificationTypeEnum.NATIONAL_ID && (
                      <>
                        {' '}
                        <div className="flex-1">
                          <div
                            className={`${
                              formik.values.identificationType ===
                              IdentificationTypeEnum.INTERNATION_PASSPORT
                                ? ''
                                : 'flex'
                            }  items-center gap-3`}
                          >
                            {formik.values.identificationType !==
                              IdentificationTypeEnum.NATIONAL_ID && (
                              <TextField
                                name="identificationId"
                                placeholder=""
                                value={formik.values.identificationId}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                label="Enter Identification Number"
                                size="lg"
                                className="rounded-2xl border-[1.5px] border-gray-300"
                              />
                            )}
                            {formik.values.identificationType ===
                              IdentificationTypeEnum.INTERNATION_PASSPORT && (
                              <div className="mt-3">
                                <TextField
                                  name="passportLastName"
                                  placeholder=""
                                  value={formik.values.passportLastName}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  label="Enter Passport Last Name"
                                  size="lg"
                                  className="rounded-2xl border-[1.5px] border-gray-300"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-center text-gray-600">
                          <span className="font-bold text-[#8181A5]">or</span>
                        </div>
                      </>
                    )}
                    <div className="flex-1">
                      <label
                        htmlFor="file_upload"
                        className="text-[12px] font-medium text-[#353535]"
                      >
                        Upload Image of your ID
                      </label>
                      <div className="relative w-full">
                        <input
                          type="file"
                          hidden
                          id="file_upload"
                          accept=".png, .jpg, .jpeg"
                          onChange={handleImage}
                        />
                        <input
                          type="search"
                          disabled
                          style={{ zIndex: 1 }}
                          value={file?.name || formik.values.base64FileName}
                          className="block w-full rounded-2xl border-[1.5px] border-[#DEE1E5] bg-gray-50 py-3.5 pr-20 text-sm text-gray-900 focus:border-[#DEE1E5] focus:ring-[#DEE1E5]"
                        />
                        <label
                          role="button"
                          htmlFor="file_upload"
                          style={{ zIndex: 20 }}
                          className="absolute bottom-0 right-0 z-10 w-[6rem] cursor-pointer rounded-2xl border border-[#7F8B94] bg-[#7F8B944D] px-4 py-[14.8px] text-center text-sm font-medium text-[#5C6B7E] focus:outline-none focus:ring-4 focus:ring-blue-300"
                        >
                          Upload
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs capitalize text-red-600">
                    {formik.errors.identificationBase64 && (
                      <p>
                        At least one of Identification Number or identification
                        image should be empty
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex w-full items-end justify-between">
            <Button
              variant="outline"
              size="md"
              className="mt-5 w-[9rem] rounded p-[0.1rem] text-primary-900"
              label="Previous"
              type="button"
              onClick={() => onPrevious()}
            ></Button>
            <div className="mt-10 flex items-end justify-end gap-8">
              {parentPath !== 'dashboard' && (
                <button
                  type="submit"
                  className="font-circular text-[15px] font-bold text-[#353535] underline"
                  onClick={() => {
                    onContinueLater();
                    setIsLater(true);
                  }}
                >
                  Save and Continue Later
                </button>
              )}
              <Button
                variant="primary"
                size="md"
                className="mt-5 w-[9rem] rounded p-[0.1rem]"
                label="Proceed"
                type="submit"
                disabled={
                  !file?.name &&
                  !formik.values.identificationBase64 &&
                  !formik.values.identificationId &&
                  !formik.values.base64FileName
                }
                loading={isSubmitting}
              ></Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
